import { Component, OnInit, ViewChild, OnDestroy, inject } from '@angular/core';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import { FilterConfig, FiltersConfig } from '@next/core-lib/components';
import { FilterTypes } from '@next/core-lib/components';
import { LocaleService } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';

import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { map, withLatestFrom, takeUntil, shareReplay, startWith } from 'rxjs/operators';

import { CropAid } from '../proprietary-fertilizer/models/proprietary-fertilizer.model';
import { ProprietaryFertilizerFormService } from '../proprietary-fertilizer/services/proprietary-fertilizer-form.service';
import * as ProprietaryFertilizerActions from '../proprietary-fertilizer/store/proprietary-fertilizer.actions';
import {
  selectProprietaryFertilizers,
  selectActiveOriginalFertilizers,
} from '../proprietary-fertilizer/store/proprietary-fertilizer.selectors';
import { AppState } from '../shared/models/app.model';
import { setCropYear } from '../store/app.actions';
import {
  getCropYear,
  getAvailableCropYears,
  getDefaultCropYear,
  isPreviousCropYear,
} from '../store/app.selectors';

@Component({
  selector: 'app-proprietary-fertilizers-overview',
  templateUrl: './proprietary-fertilizers-overview.component.html',
  styleUrls: ['./proprietary-fertilizers-overview.component.scss'],
  standalone: false,
})
export class ProprietaryFertilizersOverviewComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);
  private readonly proprietaryFertilizerFormService = inject(ProprietaryFertilizerFormService);
  private readonly locale = inject(LocaleService);

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  private readonly cropYearChanged$ = new Subject<number>();
  private readonly searchChanged$ = new Subject<string>();
  private readonly availableCropYears$ = this.store.pipe(select(getAvailableCropYears));
  private readonly cropYear$ = this.store.pipe(select(getCropYear));
  private readonly defaultCropYear$ = this.store.pipe(select(getDefaultCropYear));
  private readonly proprietaryFertilizers$ = this.store.pipe(select(selectProprietaryFertilizers));
  private readonly activeOriginalFertilizers$ = this.store.pipe(
    select(selectActiveOriginalFertilizers),
  );
  pageSize = 10;
  displayedColumns = [
    'id',
    'name',
    'nutritionalCompositionN',
    'nutritionalCompositionP2O5',
    'nutritionalCompositionK2O',
    'productType',
    'actions',
  ];
  @ViewChild('paginator') paginator: MatPaginator;
  page$ = new BehaviorSubject<PageEvent>({
    pageIndex: 0,
    pageSize: this.pageSize,
    length: null,
  });
  filterConfig: FilterConfig<FiltersConfig> = {
    filters: {
      year: {
        label: 'year',
        type: FilterTypes.Select,
        options: this.availableCropYears$.pipe(
          map((availableYears) =>
            availableYears.map((availableYear) => ({
              value: availableYear,
              viewValue: availableYear.toString(),
            })),
          ),
        ),
        value: this.cropYear$,
        defaultValue: this.defaultCropYear$,
        changes: this.cropYearChanged$,
      },
      search: {
        label: 'search',
        type: FilterTypes.Text,
        changes: this.searchChanged$,
      },
    },
  };
  filterValue$ = this.searchChanged$.pipe(
    map((value: string) => value || ''),
    startWith(''),
  );
  filteredProprietaryFertilizers$ = combineLatest([
    this.filterValue$,
    this.proprietaryFertilizers$,
  ]).pipe(
    map(([filter, proprietaryFertilizers]) => {
      if (this.paginator) {
        this.paginator.firstPage();
      }

      return filter
        ? proprietaryFertilizers.filter(
            ({ name }) => name.toLowerCase().indexOf(filter.toLowerCase()) !== -1,
          )
        : proprietaryFertilizers;
    }),
    shareReplay(1),
  );
  pagedProprietaryFertilizers$ = combineLatest([
    this.filteredProprietaryFertilizers$,
    this.page$,
  ]).pipe(
    map(([proprietaryFertilizers, { pageIndex, pageSize }]) =>
      proprietaryFertilizers.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize),
    ),
  );
  filterEnabled$ = this.filterValue$.pipe(map((value) => !!value));
  isMobile$ = this.breakpoint.ltLg$;
  isPreviousYear$ = this.store.pipe(select(isPreviousCropYear));

  ngOnInit(): void {
    this.cropYearChanged$
      .pipe(withLatestFrom(this.cropYear$), takeUntil(this.destroyed$))
      .subscribe(([newYear, existingYear]) => {
        if (newYear && newYear !== existingYear) {
          this.store.dispatch(setCropYear({ cropYear: newYear }));
        }
      });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  getTitle(): Observable<string> {
    return this.store
      .pipe(select(UserSelectors.selectCustomer))
      .pipe(
        map(
          (customer) =>
            this.locale.translate('title') +
            (customer
              ? ` ${this.locale.translate('of')} ${customer.name} ${customer.externalId}`
              : ''),
        ),
      );
  }

  createProprietaryFertilizer() {
    const proprietaryFertilizer =
      this.proprietaryFertilizerFormService.createEmptyProprietaryFertilizer();
    this.store.dispatch(
      ProprietaryFertilizerActions.openProprietaryFertilizerDialog({ proprietaryFertilizer }),
    );
  }

  editProprietaryFertilizer(cropAid: CropAid) {
    const proprietaryFertilizer =
      this.proprietaryFertilizerFormService.createEmptyProprietaryFertilizerFromCropAid(cropAid);
    this.store.dispatch(
      ProprietaryFertilizerActions.openProprietaryFertilizerDialog({ proprietaryFertilizer }),
    );
  }

  page(event: PageEvent) {
    this.page$.next(event);
  }

  getOriginalCropAidName(originalCropAidId: number): Observable<string> {
    return this.activeOriginalFertilizers$.pipe(
      map((cropAids) => cropAids?.find((cropAid) => cropAid.id === originalCropAidId)?.name),
    );
  }

  roundCompositionValue(value: number | null) {
    if (typeof value !== 'number') {
      return '';
    }
    return Math.round(value * 1000) / 1000;
  }
}
